import { TestIdProvider } from '@ori/testid-generator';
import { useCallback, type FC, useMemo, useState, useEffect } from 'react';

import { LanguageSelectModal } from '../components';
import { APP_NAME, APP_TEAM_NAME } from '../constants';
import { ConfigurationProvider } from '../contexts';
import { useTenantLanguages } from '../hooks/useTenantLanguages';
import type { LanguageSwitcherPopupProps, Language } from '../models';
import {
  changeLanguage,
  getDefaultIsOpenedState,
  getLanguageCookieValue,
  hasLanguageInUrl,
  isDefaultLanguage,
} from '../utils';
import { ErrorBoundary } from './ErrorBoundary';

/**
 * Ensures that language cookie is set when there is language in URL
 */
export const useVerifyCookies = (currentLanguage: string, tenant: string, languages?: Language[]) =>
  useEffect(() => {
    const currentLanguageInfo = languages?.find(({ code }) => currentLanguage === code);

    if (languages && currentLanguageInfo && hasLanguageInUrl(languages) && !getLanguageCookieValue()) {
      changeLanguage(currentLanguageInfo, currentLanguage, tenant, false);
    }
  }, [languages, currentLanguage, tenant]);

/**
 * Language switcher modal component that shows and changes language
 */
export const LanguageSwitcherPopup: FC<LanguageSwitcherPopupProps> = (props) => {
  const { prefetchedLanguages, currentLanguage, tenant, staticApiUrl } = props;
  const { data: fetchedLanguages } = useTenantLanguages(staticApiUrl, tenant, !!prefetchedLanguages);
  const availableLanguages = useMemo(
    () => prefetchedLanguages ?? fetchedLanguages,
    [fetchedLanguages, prefetchedLanguages],
  );
  const [modalIsOpen, setModalIsOpen] = useState(getDefaultIsOpenedState(availableLanguages ?? []));

  useVerifyCookies(currentLanguage, tenant, availableLanguages);

  // This is for the case that languages changed in some way and the modal is not shown.
  useEffect(() => {
    if (!modalIsOpen && availableLanguages) {
      setModalIsOpen(getDefaultIsOpenedState(availableLanguages));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps -- we don't need to listen to "modalIsOpen"
  }, [availableLanguages]);

  const onCloseCallback = useCallback(() => {
    setModalIsOpen(false);
  }, []);

  const handleLanguageChange = useCallback(
    (nextLanguage: Language) => {
      changeLanguage(
        nextLanguage,
        currentLanguage,
        tenant,
        // If user selected default language then there is no reason to reload the page again
        !isDefaultLanguage(nextLanguage),
      );

      onCloseCallback();
    },
    [currentLanguage, onCloseCallback, tenant],
  );

  const configurationProviderValue = useMemo(
    () => ({ ...props, languages: availableLanguages }),
    [props, availableLanguages],
  );

  return (
    <ErrorBoundary>
      <ConfigurationProvider value={configurationProviderValue}>
        <TestIdProvider
          team={APP_TEAM_NAME}
          project={APP_NAME}
        >
          <LanguageSelectModal
            isOpen={modalIsOpen}
            onLanguageChange={handleLanguageChange}
            onClose={onCloseCallback}
          />
        </TestIdProvider>
      </ConfigurationProvider>
    </ErrorBoundary>
  );
};
